import { NumericInputCharacters } from '../enums/numeric-input-characters';
import { isNullOrUndefined } from './comparison';

export function formatNumericInput(value?: number | null, precision?: number | null, currency?: boolean | null): string {
	const defaultPrecision = value?.toString().includes(NumericInputCharacters.period) ? 2 : 0;
	const calculatedPrecision = precision ?? defaultPrecision;

	if (currency) {
		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'usd',
			minimumFractionDigits: calculatedPrecision,
			maximumFractionDigits: calculatedPrecision
		}).format(value as number);
	}

	if (!isNullOrUndefined(value)) {
		return new Intl.NumberFormat('en-US', {
			style: 'decimal',
			minimumFractionDigits: calculatedPrecision,
			maximumFractionDigits: calculatedPrecision
		}).format(value as number);
	}
	return '';
}

export function restrictToNumericInput(evt: KeyboardEvent): void {
	const keysAllowed: string[] = Object.values(NumericInputCharacters);
	const keyPressed: string = evt.key;
	if (!keysAllowed.includes(keyPressed)) {
		evt.preventDefault();
	}
}

export function formatValidationInputStyle(
	showIcon: boolean,
	showValidation: boolean,
	displayValue: string,
	minLength: number | null,
	percent?: boolean | false,
	state?: boolean | null
): string {
	if (!showIcon && state !== null) {
		// Covers if state is false/true, and the showIcon prop is not true
		return 'disable-icon';
	}
	if (!showIcon && (showValidation || minLength !== null)) {
		// if icon doesn't want to be shown, but wants our validation text/border
		return 'disable-icon';
	} else if ((showValidation || minLength !== null) && showIcon) {
		if (displayValue === '' && !showIcon) {
			// default input to make padding align to right
			return '';
		}
		if (showIcon && percent) {
			// If styling has percent fa-icon and validation icon.
			if (displayValue === '' && state === null) {
				// Does not move the padding to account for the validation icon since input is empty.
				return '';
			}
			return 'align-text-with-percent';
		}
		// css to allow our validation style with icon validation so icon doesn't clash with input text
		return 'align-text-with-icon';
	}
	return '';
}

export function dynamicValidationPercentString(min: number | null, max: number | null, minLength: number | null = null, maxLength: number | null): string {
	// if tux-percent-input displays percent sign.
	if (min == null) {
		if (max !== null) {
			// If min is null, but there is a max value.
			if (minLength !== null) {
				if (maxLength !== null) {
					// If min is null, max has a value, maxLength & minLength has a value.
					return `below ${formatNumericInput(max)}%, a minimum length of ${minLength} and max length of ${maxLength}.`;
				}
				if (maxLength == null) {
					// If min/maxLength is null, max/minLength have values.
					return `below ${formatNumericInput(max)}%, and a minimum length of ${minLength}.`;
				}
			} else if (minLength == null) {
				if (maxLength !== null) {
					// If min is null, max has a value, minLength is null & maxLength has a value.
					return `below ${formatNumericInput(max)}%, and max length of ${maxLength}.`;
				}
			}
			// min/minLength/maxLength is null, but there is a max value.
			return `below ${formatNumericInput(max)}%.`;
		}

		if (max == null) {
			if (minLength !== null) {
				if (maxLength !== null) {
					// min/max is null, but there is a minLength/maxLength value.
					return `of minimum length of ${minLength}, and max length of ${maxLength}.`;
				}
				if (maxLength == null) {
					// min/max/maxLength is null, but there is a minLength value.
					return `of minimum length of ${minLength}.`;
				}
			} else if (minLength == null) {
				if (maxLength !== null) {
					// min/max/minLength is null, but there is a maxLength value.
					return `of max length of ${maxLength}.`;
				}
			}
		}
	}

	if (min !== null) {
		if (max !== null) {
			if (minLength !== null) {
				if (maxLength !== null) {
					// If all 4 props have values.
					return `between ${formatNumericInput(min)}% and ${formatNumericInput(max)}%, a minimum length of ${minLength}, and max length of ${maxLength}.`;
				}
				if (maxLength == null) {
					// If maxLength is null, but min/max/minLength have values.
					return `between ${formatNumericInput(min)}% and ${formatNumericInput(max)}%, and a minimum length of ${minLength}.`;
				}
			} else if (minLength == null) {
				if (maxLength !== null) {
					// If minLength is null, but min/max/maxLength have values.
					return `between ${formatNumericInput(min)}% and ${formatNumericInput(max)}%, and max length of ${maxLength}.`;
				}
			}
			// If minLength/maxLength are null, but min/max have values.
			return `between ${formatNumericInput(min)}% and ${formatNumericInput(max)}% `;
		}

		if (max == null) {
			if (minLength !== null) {
				if (maxLength !== null) {
					// max is null, but min/minLength/maxLength have values.
					return `above ${formatNumericInput(min)}%, of minimum length of ${minLength}, and max length of ${maxLength}.`;
				}
				if (maxLength == null) {
					// max/maxLength are null, but min/minLength have values.
					return `above ${formatNumericInput(min)}%, and a minimum length of ${minLength}.`;
				}
			} else if (minLength == null) {
				if (maxLength !== null) {
					// max/minLength are null, but min/maxLength have values.
					return `above ${formatNumericInput(min)}%, and of max length of ${maxLength}.`;
				}
			}
			// If minLength/maxLength are null, but min has a value.
			return `above ${formatNumericInput(min)}%.`;
		}
	}
	return '';
}

export function dynamicValidationMoneyString(min: number | null, max: number | null, minLength: number | null, maxLength: number | null): string {
	// if tux-input-money displays money sign
	if (min == null) {
		if (max !== null) {
			// If min is null, but there is a max value.
			if (minLength !== null) {
				if (maxLength !== null) {
					// If min is null, max has a value, maxLength & minLength has a value.
					return `below ${formatNumericInput(max, null, true)}, a minimum length of ${minLength}, and max length of ${maxLength}.`;
				}
				if (maxLength == null) {
					// If min/maxLength is null, max/minLength have values.
					return `below ${formatNumericInput(max, null, true)}, and a minimum length of ${minLength}.`;
				}
			} else if (minLength == null) {
				if (maxLength !== null) {
					// If min is null, max has a value, minLength is null & maxLength has a value.
					return `below ${formatNumericInput(max, null, true)}, and max length of ${maxLength}.`;
				}
			}
			// min/minLength/maxLength is null, but there is a max value.
			return `below ${formatNumericInput(max, null, true)}.`;
		}

		if (max == null) {
			if (minLength !== null) {
				if (maxLength !== null) {
					// min/max is null, but there is a minLength/maxLength value.
					return `of minimum length of ${minLength}, and max length of ${maxLength}.`;
				}
				if (maxLength == null) {
					// min/max/maxLength is null, but there is a minLength value.
					return `of minimum length of ${minLength}.`;
				}
			} else if (minLength == null) {
				if (maxLength !== null) {
					// min/max/minLength is null, but there is a maxLength value.
					return `of max length of ${maxLength}.`;
				}
			}
		}
	}

	if (min !== null) {
		if (max !== null) {
			if (minLength !== null) {
				if (maxLength !== null) {
					// If all 4 props have values.
					return `between ${formatNumericInput(min, null, true)} and ${formatNumericInput(
						max,
						null,
						true
					)}, a minimum length of ${minLength}, and max length of ${maxLength}.`;
				}
				if (maxLength == null) {
					// If maxLength is null, but min/max/minLength have values.
					return `between ${formatNumericInput(min, null, true)} and ${formatNumericInput(max, null, true)}, and a minimum length of ${minLength}.`;
				}
			} else if (minLength == null) {
				if (maxLength !== null) {
					// If minLength is null, but min/max/maxLength have values.
					return `between ${formatNumericInput(min, null, true)} and ${formatNumericInput(max, null, true)}, and max length of ${maxLength}.`;
				}
			}
			// If minLength/maxLength are null, but min/max have values.
			return `between ${formatNumericInput(min, null, true)} and ${formatNumericInput(max, null, true)} `;
		}

		if (max == null) {
			if (minLength !== null) {
				if (maxLength !== null) {
					// max is null, but min/minLength/maxLength have values.
					return `above ${formatNumericInput(min, null, true)}, of minimum length of ${minLength}, and max length of ${maxLength}.`;
				}
				if (maxLength == null) {
					// max/maxLength are null, but min/minLength have values.
					return `above ${formatNumericInput(min, null, true)}, and a minimum length of ${minLength}.`;
				}
			} else if (minLength == null) {
				if (maxLength !== null) {
					// max/minLength are null, but min/maxLength have values.
					return `above ${formatNumericInput(min, null, true)}, and of max length of ${maxLength}.`;
				}
			}
			// If minLength/maxLength are null, but min has a value.
			return `above ${formatNumericInput(min, null, true)}.`;
		}
	}
	return '';
}

export function dynamicValidationString(min: number | null, max: number | null, minLength: number | null, maxLength: number | null): string {
	if (min == null) {
		if (max !== null) {
			if (minLength !== null) {
				if (maxLength !== null) {
					// If min is null, max has a value, maxLength & minLength has a value.
					return `below ${formatNumericInput(max)}, a minimum length of ${minLength}, and max length of ${maxLength}.`;
				}
				if (maxLength == null) {
					// If min/maxLength is null, max/minLength have values.
					return `below ${formatNumericInput(max)}, and a minimum length of ${minLength}.`;
				}
			} else if (minLength == null) {
				if (maxLength !== null) {
					// If min is null, max has a value, minLength is null & maxLength has a value.
					return `below ${formatNumericInput(max)}, and max length of ${maxLength}.`;
				}
			}
			// min/minLength/maxLength is null, but there is a max value.
			return `below ${formatNumericInput(max)}.`;
		}

		if (max == null) {
			if (minLength !== null) {
				if (maxLength !== null) {
					// min/max is null, but there is a minLength/maxLength value.
					return `of minimum length of ${minLength}, and max length of ${maxLength}.`;
				}
				if (maxLength == null) {
					// min/max/maxLength is null, but there is a minLength value.
					return `of minimum length of ${minLength}.`;
				}
			} else if (minLength == null) {
				if (maxLength !== null) {
					// min/max/minLength is null, but there is a maxLength value.
					return `of max length of ${maxLength}.`;
				}
			}
		}
	}

	if (min !== null) {
		if (max !== null) {
			if (minLength !== null) {
				if (maxLength !== null) {
					// If all 4 props have values.
					return `between ${formatNumericInput(min)} and ${formatNumericInput(max)}, a minimum length of ${minLength}, and max length of ${maxLength}.`;
				}
				if (maxLength == null) {
					// If maxLength is null, but min/max/minLength have values.
					return `between ${formatNumericInput(min)} and ${formatNumericInput(max)}, and a minimum length of ${minLength}.`;
				}
			} else if (minLength == null) {
				if (maxLength !== null) {
					// If minLength is null, but min/max/maxLength have values.
					return `between ${formatNumericInput(min)} and ${formatNumericInput(max)}, and max length of ${maxLength}.`;
				}
			}
			// If minLength/maxLength are null, but min/max have values.
			return `between ${formatNumericInput(min)} and ${formatNumericInput(max)} `;
		}

		if (max == null) {
			if (minLength !== null) {
				if (maxLength !== null) {
					// max is null, but min/minLength/maxLength have values.
					return `above ${formatNumericInput(min)}, of minimum length of ${minLength}, and max length of ${maxLength}.`;
				}
				if (maxLength == null) {
					// max/maxLength are null, but min/minLength have values.
					return `above ${formatNumericInput(min)}, and a minimum length of ${minLength}.`;
				}
			} else if (minLength == null) {
				if (maxLength !== null) {
					// max/minLength are null, but min/maxLength have values.
					return `above ${formatNumericInput(min)}, and of max length of ${maxLength}.`;
				}
			}
			// If minLength/maxLength are null, but min has a value.
			return `above ${formatNumericInput(min)}.`;
		}
	}
	return '';
}

// Logic to validate if it meets the min/max ranges of the passed props
export function validateMinMaxProps(min: string | number | null, max: string | number | null, value: string | null): boolean {
	if (value) {
		if (min !== null && max !== null && min <= Number(value) && max >= Number(value)) {
			// If a min and max prop are passed, checks validation
			return true;
		}
		if (min !== null && min && (max === null || max === Number.MAX_SAFE_INTEGER) && Number(value) >= min) {
			// If a min prop are passed, without max, checks validation
			return true;
		}
		if (max !== null && max && (min === null || min === Number.MAX_SAFE_INTEGER) && Number(value) <= max) {
			// If a max prop are passed, without min, checks validation
			return true;
		}
		return false;
	}
	return false;
}

export function validateMinLength(displayValue: string | number, minLength: number | number | null): boolean {
	if (displayValue >= Number(minLength)) {
		return true;
	}
	return false;
}

export function validateMaxLength(displayValue: string | number | null, maxLength: number | number | null): boolean {
	if (displayValue !== null && maxLength !== null) {
		if (displayValue > maxLength) {
			return false;
		}
	}
	return true;
}

export function rangeValidation(
	min: string | number | null,
	max: string | number | null,
	minLength: number | number | null,
	displayValue: string | null,
	emitValueFunction: (arg: boolean) => void,
	maxLength: number | null
): boolean {
	// Display Value length is used for min/max length validation
	let displayValueLength = 0;

	if (displayValue !== null) {
		displayValueLength = displayValue.toString().length;
	}

	let minLengthValid = false;
	let maxLengthValid = false;

	if (displayValue !== null) {
		// Min length validation.
		minLengthValid = validateMinLength(displayValueLength, minLength);
		// Max length validation
		maxLengthValid = validateMaxLength(displayValueLength, maxLength);
	}

	// Validates if display value are in min/max range.
	const minMaxValidation = validateMinMaxProps(min, max, displayValue);

	if (minLength == null) {
		// Covers if minLength is null, but there is a maxLength value.
		if (maxLength !== null) {
			if (min == null) {
				if (max !== null) {
					// This condition means we are checking the range & min/max length validation.
					if (minMaxValidation && maxLengthValid) {
						emitValueFunction(true);
						return true;
					} else {
						emitValueFunction(false);
						return false;
					}
				}
			}
			if (min !== null) {
				if (max !== null) {
					// This condition means minLength is null, and maxLength/min/max have values & checks validation.
					if (minMaxValidation && maxLengthValid) {
						emitValueFunction(true);
						return true;
					} else {
						emitValueFunction(false);
						return false;
					}
				}
			}
			emitValueFunction(maxLengthValid);
			return maxLengthValid;
		}

		if (max == null && min == null) {
			// Case to cover if min/max/minLength/maxLength props values are null returns true.
			emitValueFunction(true);
			return true;
		}
	}

	if (minLength !== null) {
		// Min/max length booleans to verify validation.
		if (maxLength !== null) {
			// Validating max length
			maxLengthValid = validateMaxLength(displayValueLength, maxLength);
			if (max === null && min === null) {
				// This condition means we are checking the input-text min/max length
				if (minLengthValid && maxLengthValid) {
					emitValueFunction(true);
					return true;
				} else {
					emitValueFunction(false);
					return false;
				}
			} else if (minLengthValid && maxLengthValid && minMaxValidation) {
				// This condition checks, the input numbers to verify range and length validation
				emitValueFunction(true);
				return true;
			} else {
				emitValueFunction(false);
				return false;
			}
		}

		if ((min === null || max === null) && minLength !== null) {
			// Verifies if a min/max prop is passed to check number range. If none, then validates minLength.
			emitValueFunction(minLengthValid);
			return minLengthValid;
		}
		if (!minLengthValid || !minMaxValidation) {
			// If either minLength or min max range aren't valid, returns false.
			emitValueFunction(false);
			return false;
		} else {
			// If passes previous if, then minLength is met, and min/max meet range.
			emitValueFunction(true);
			return true;
		}
	} else {
		emitValueFunction(minMaxValidation);

		if (validateMinMaxProps(min, max, displayValue)) {
			emitValueFunction(true);
			return true;
		} else if (min == null && max === Number.MAX_SAFE_INTEGER) {
			emitValueFunction(true);
			return true;
		} else {
			emitValueFunction(false);
			return false;
		}
	}
}
